import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Section } from '../fx/AccentContext';
import { MaskedLines } from '../fx/MaskedLines';
import VisionMissionBand from '../page/VisionMissionBand';
import LivingImage from '../fx/LivingImage';
import { introDelay } from '../fx/Preloader';
import { scrollToSection } from '../fx/SmoothScroll';
import { MagneticCTA, PADX } from './ui';
import { EASE_EXPO } from '../fx/ease';
import { tagline, vision } from '../../data/identity';

const metaItems = [
  { label: 'Est. 2023', wide: false },
  { label: 'Tokyo', wide: false },
  { label: 'ROOTED IN UTOKYO', wide: true },
  { label: 'Boost Group', wide: true },
];

const HeroSection = () => {
  const reduce = useReducedMotion();
  const bodyRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: bodyRef,
    offset: ['start start', 'end start'],
  });
  // 控えめなパララックス: 写真はゆっくり残り、テキストは先に退く
  const photoY = useTransform(scrollYProgress, [0, 1], ['0%', '10%']);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '-14%']);
  const d = introDelay();

  return (
    <Section id="mission" index={0} accent="blue" className="relative">
      {/* ヒーロー本体 */}
      <div
        ref={bodyRef}
        className="relative flex min-h-[calc(100svh-0px)] flex-col justify-between overflow-hidden pt-[64px] md:pt-[72px]"
      >
        {/* 建築写真: lg以上で右半分に固定。カーソルで光が入る */}
        <motion.div
          className="absolute bottom-0 right-0 top-[64px] hidden w-[52%] md:top-[72px] lg:block"
          style={reduce ? undefined : { y: photoY }}
        >
          <LivingImage
            src="/img/architecture/blue/p01-blue-column-courtyard.jpg"
            alt="コンクリートの中庭とブルーの柱 — SALT2のブランドアーキテクチャ"
            mode="lit"
            radius={300}
            curtain
            sweep
            eager
            revealDelay={d + 0.15}
            className="h-full w-full"
            imgClassName="object-cover object-[38%_50%]"
          />
        </motion.div>

        {/* テキスト */}
        <motion.div
          className={`relative z-10 flex flex-1 flex-col justify-center py-14 md:py-16 ${PADX}`}
          style={reduce ? undefined : { y: textY }}
        >
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE_EXPO, delay: d }}
            className="text-[11px] font-semibold uppercase tracking-label text-gray-600 md:text-xs"
          >
            PRODUCT / ENABLEMENT / CONSULTING — TOKYO
          </motion.div>

          <MaskedLines
            as="h1"
            per="char"
            lines={vision.enLines}
            lineClassName={(i) => (i === 2 ? 'pl-[0.55em]' : '')}
            className="display-type mt-8 text-[17vw] text-ink sm:text-[15vw] lg:text-[10.2vw] xl:text-[9.6vw] 2xl:text-[152px]"
            delay={d + 0.15}
            stagger={0.13}
            duration={0.85}
          />

          <MaskedLines
            lines={[[vision.jp], [tagline]]}
            className="mt-9 max-w-[34em] text-[15px] font-medium leading-relaxed tracking-display-ja text-gray-700 md:text-[17px]"
            delay={d + 0.75}
            duration={0.8}
          />

          <motion.div
            className="mt-10 flex flex-row flex-wrap gap-3 sm:gap-4 md:mt-12"
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE_EXPO, delay: d + 0.95 }}
          >
            <MagneticCTA
              onClick={() => scrollToSection('business')}
              variant="outline"
              cursorLabel="VIEW"
            >
              事業を見る
            </MagneticCTA>
            <MagneticCTA to="/careers" variant="outline" icon="up" cursorLabel="JOIN">
              採用サイト
            </MagneticCTA>
          </motion.div>
        </motion.div>

        {/* モバイル: 写真をテキスト下に */}
        <div className="lg:hidden">
          <LivingImage
            src="/img/architecture/blue/p01-blue-column-courtyard.jpg"
            alt="コンクリートの中庭とブルーの柱"
            mode="reveal"
            curtain
            sweep={false}
            eager
            className="aspect-[16/10] w-full"
          />
        </div>
      </div>

      {/* メタストリップ */}
      <div className="border-y border-line">
        <div className="grid grid-cols-2 md:grid-cols-4">
          {metaItems.map((m, i) => (
            <motion.div
              key={m.label}
              className={`flex items-center justify-center overflow-hidden border-line px-3 py-5 text-center ${
                ['', 'border-l', 'max-md:border-t md:border-l', 'border-l max-md:border-t'][i]
              }`}
              initial={reduce ? false : 'hidden'}
              whileInView="show"
              viewport={{ once: true }}
            >
              <motion.span
                className="block text-[10px] font-semibold uppercase tracking-label text-gray-600 md:text-[11px]"
                variants={{
                  hidden: { y: '120%' },
                  show: {
                    y: '0%',
                    transition: { duration: 0.7, ease: EASE_EXPO, delay: 0.1 + i * 0.07 },
                  },
                }}
              >
                {m.label}
              </motion.span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* 01 — VISION & MISSION。会社概要と同じ黒い帯（2026-10-18 代表指示「スクショのものを最初のページにも載せたい」） */}
      <div className="bg-gray-950">
        <VisionMissionBand no="01" aboutLink showEnglish={false} />
      </div>
    </Section>
  );
};

export default HeroSection;
